import { legendDisplayName } from "@openrift/shared/card-name";
import { RENAMED_LANGUAGES } from "@openrift/shared/well-known";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { RouteErrorFallback } from "@/components/error-message";
import { publicPromoListQueryOptions } from "@/features/cards/lib/public-promos-queries";
import { cleanedSearchForRedirect, filterSearchSchema } from "@/features/cards/lib/search-schemas";
import { initQueryOptions } from "@/lib/init-queries";
import { collectionPageJsonLd, seoHead } from "@/lib/seo";
import { getSiteUrl } from "@/lib/site-config";

interface JsonLdItem {
  name: string;
  url: string;
}

function promosDescription(languageName: string): string {
  return `Browse all ${languageName} promotional card printings for the Riftbound trading card game, grouped by promo type.`;
}

export const Route = createFileRoute("/_app/promos_/$language")({
  validateSearch: filterSearchSchema,
  beforeLoad: ({ search, location, params }) => {
    // Old language codes would 404 in the loader; redirect to the current code.
    const renamed = RENAMED_LANGUAGES[params.language.toUpperCase()];
    if (renamed) {
      throw redirect({
        to: "/promos/$language",
        params: { language: renamed },
        search,
        replace: true,
      });
    }

    // Strip unknown / malformed search params so bots following share links
    // land on a clean canonical URL.
    const cleaned = cleanedSearchForRedirect(filterSearchSchema, search, location.searchStr);
    if (cleaned) {
      throw redirect({
        to: "/promos/$language",
        params: { language: params.language },
        search: cleaned,
        replace: true,
      });
    }
  },
  head: ({ params, loaderData }) => {
    const siteUrl = getSiteUrl();
    const path = `/promos/${params.language}`;
    // beforeLoad throws a redirect, which narrows loaderData to never here.
    const data = loaderData as { items: JsonLdItem[]; languageName: string } | undefined;
    const items = data?.items ?? [];
    const languageName = data?.languageName ?? params.language;
    const description = promosDescription(languageName);
    const head = seoHead({
      siteUrl,
      title: `Riftbound Promo Cards in ${languageName}`,
      description,
      path,
    });

    return {
      ...head,
      scripts: [
        collectionPageJsonLd({
          siteUrl,
          name: `Riftbound Promo Cards in ${languageName}`,
          description,
          path,
          items,
        }),
      ],
    };
  },
  loader: async ({ params, context }) => {
    // Only the JSON-LD items and language name are returned: the loaderData return value is
    // serialized on top of the dehydrated queries, doubling the payload.
    const [data, init] = await Promise.all([
      context.queryClient.query({
        ...publicPromoListQueryOptions(params.language),
        staleTime: "static",
      }),
      context.queryClient.query({ ...initQueryOptions, staleTime: "static" }),
    ]);
    if (data.printings.length === 0) {
      throw notFound();
    }

    const seenCardIds = new Set<string>();
    const items: JsonLdItem[] = [];
    for (const printing of data.printings) {
      if (seenCardIds.has(printing.cardId)) {
        continue;
      }
      seenCardIds.add(printing.cardId);
      const card = data.cards[printing.cardId];
      if (!card) {
        continue;
      }
      items.push({ name: legendDisplayName(card), url: `/cards/${card.slug}` });
    }
    const languageName =
      init.enums.languages?.find((row) => row.slug === params.language)?.label ?? params.language;
    return { items, languageName };
  },
  errorComponent: RouteErrorFallback,
});
